import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Actor } from '../domain/permissions';
import { buildGraph, type FamilyGraph } from '../domain/relationships';
import {
  DEFAULT_GOVERNANCE, DEFAULT_TREE_SETTINGS,
  type Family, type FamilyMembership, type FamilyPhoto, type Governance, type Lang, type Location, type MemorySubject, type Notification, type PersonAccountLink, type PersonApproval, type PersonPlace, type PersonPortrait, type PersonWithNames, type ProfileManagementGrant, type Recipe, type Relationship, type Session, type Story,
  type StoryContribution, type StoryPerson, type StoryQuestion, type TreeSettings, type UUID,
} from '../domain/types';
import { occasionsToday, upcomingOccasions, type Occasion } from '../domain/occasions';
import { useI18n } from '../i18n';
import { useLocalState } from '../lib/hooks';
import { useRepos } from '../repositories';
import { localDateISO, whereIsAt } from '../domain/places';
import { shabbatAt } from '../domain/shabbat';
import { DEFAULT_EXPERIENCE, isExperienceMode, isFeatureOn, type ExperienceMode, type Feature } from './experience';

export type MotionPref = 'system' | 'reduce' | 'full';
export type TextSizePref = 'normal' | 'large' | 'xlarge';
/*
 * There is no appearance setting any more: the app is dark, everywhere, always.
 *
 * Jay: *"in settings, could we remove the appearance? Let's always keep it to
 * dark appearance."* It was three buttons that changed nothing anybody had
 * complained about, in a panel that had grown long enough to hide the settings
 * that do matter. The light token set stays in styles/tokens.css — taking it
 * out is a separate, larger piece of work, and leaving it costs nothing but the
 * bytes — but nothing sets `data-theme` to anything but `dark` now.
 */
/** The overlays that open on top of whatever screen you are on (never a screen of their own). */
export type PanelKind = 'settings' | 'profile' | 'notifications';
/** What the Add tab is adding: its menu, a photo, an everyday event or a family milestone (a story goes to the recording screen). */
export type AddKind = 'menu' | 'photo' | 'event' | 'milestone';
/** Why a story was opened, when it was opened for a reason rather than to be read. */
export type StoryIntent = 'play' | 'edit';

/**
 * Where a node was on the screen when it was tapped — its middle and its
 * diameter. It lives here rather than beside the constellation that uses it
 * because MemoryNodes already reads this file, and a type flowing the other way
 * would be a circle.
 */
export type NodeOrigin = { x: number; y: number; size: number };

/**
 * What the recipe editor was opened for: an existing recipe to carry on with, or
 * a new one for somebody's circle. With neither it is a new recipe of your own.
 */
export type RecipeEditorRequest = { recipeId?: UUID; personId?: UUID };

interface AppStateValue {
  session: Session | null;
  loading: boolean;
  refreshSession: () => Promise<void>;
  setSession: (s: Session | null) => void;
  actor: Actor | null;
  /** Live family data, refreshed on every committed change (this tab or another). */
  people: PersonWithNames[];
  peopleById: Map<UUID, PersonWithNames>;
  relationships: Relationship[];
  graph: FamilyGraph;
  stories: Story[];
  contributions: StoryContribution[];
  /** Who is in which story (tellers, subjects, people mentioned) — for "stories about" a person. */
  storyPeople: StoryPerson[];
  /** Questions asked of people ("ask them something"), open and answered. */
  questions: StoryQuestion[];
  /** The family's published recipes, plus this account's own drafts. They live on people's circles and nowhere else. */
  recipes: Recipe[];
  grants: ProfileManagementGrant[];
  /** Who owns which circle in the tree (active self links). */
  links: PersonAccountLink[];
  /** Every photo added to any circle (for the tree's photo badges). */
  portraits: PersonPortrait[];
  /** Everything shared in Moments (photos and events). */
  moments: FamilyPhoto[];
  /** People this account starred (highlighted in the tree). */
  stars: Set<UUID>;
  /** Every home and visit recorded for anyone (World tab, "lives in" lines). */
  places: PersonPlace[];
  locations: Location[];
  locationsById: Map<UUID, Location>;
  /** Admin-tuned growth/settling of circles and badges. */
  treeSettings: TreeSettings;
  /** Who runs the family and by what numbers (administrators by email, vouching threshold). */
  governance: Governance;
  /** Every "yes, I know them" recorded for a newly added person. */
  approvals: PersonApproval[];
  /** People whose circle is still waiting to be recognised — drawn dimmed in the tree. */
  awaitingRecognition: Set<UUID>;
  /** How the vouching stands for one person, from this account's point of view. */
  recognitionOf: (personId: UUID) => { count: number; needed: number; approved: boolean; mine: boolean };
  memberships: FamilyMembership[];
  /** The family's own record — its name, and the welcome message its administrators wrote (domain/welcome.ts). */
  family: Family | null;
  notifications: Notification[];
  unreadCount: number;
  /** Unread notifications that deserve attention: events, and posts by people you starred. */
  prominentUnreadCount: number;
  /** Whether a notification is prominent for this reader. */
  isProminent: (n: Notification) => boolean;
  pendingReviewCount: number;
  dataLoading: boolean;
  /** True once the family data has been loaded at least once for the signed-in account (dataLoading stays false during refreshes). */
  dataReady: boolean;
  reload: () => void;
  /**
   * Goes up by one every time anything here has been loaded again. A screen
   * that keeps something of its own beside these lists (a story's recordings,
   * your own drafts) refetches on this — *not* on the identity of one of the
   * lists, because a list that did not change is now the same array it was.
   */
  dataVersion: number;
  /** Which circle an account speaks for (its active link) — a lookup, so nobody has to search `links` per row. */
  personIdByAccount: Map<UUID, UUID>;
  me: PersonWithNames | null;
  online: boolean;
  pendingOps: number;
  motion: MotionPref;
  setMotion: (m: MotionPref) => void;
  textSize: TextSizePref;
  setTextSize: (s: TextSizePref) => void;
  reducedMotionActive: boolean;
  /** Advanced (the default) or Simple — see app/experience.ts for what each includes. */
  experience: ExperienceMode;
  setExperience: (m: ExperienceMode) => void;
  /** Whether a feature is part of the current mode. Screens use this rather than reading the mode. */
  has: (feature: Feature) => boolean;
  /** Administrators can take the Admin controls tab out of the dock (Settings, both modes). */
  /** This account really is an administrator (whether or not Admin mode is on). */
  canBeAdmin: boolean;
  /** Administrator powers are switched on for this device right now. */
  adminMode: boolean;
  setAdminMode: (on: boolean) => void;
  /** The panel open over the current screen, if any. */
  panel: PanelKind | null;
  openPanel: (p: PanelKind) => void;
  closePanel: () => void;
  /**
   * The welcome message, up on its board (features/welcome). It comes up by
   * itself when it is news to whoever is signed in, and from the burger menu
   * whenever anybody wants to read it again. `'news'` and `'again'` are kept
   * apart because only the first of them is a reading that has to be recorded.
   */
  welcomeOpen: 'news' | 'again' | null;
  openWelcome: (why?: 'news' | 'again') => void;
  closeWelcome: () => void;
  /**
   * Nothing in the app takes over the screen. A tapped circle — in the tree, on
   * a story, on the timeline — opens the quick card over whatever you were
   * reading; that card opens the full profile as a panel; a story opens whole,
   * with its recordings and pictures and buttons; a simcha or a birth opens its
   * pictures. They stack in the order you opened them, and closing any one of
   * them leaves you exactly where you were.
   */
  personCardId: UUID | null;
  openPersonCard: (id: UUID) => void;
  closePersonCard: () => void;
  /** Whose full profile is open as a panel. */
  profileId: UUID | null;
  openProfile: (id: UUID) => void;
  closeProfile: () => void;
  /** Which story is open — the whole thing: its recordings, pictures, reactions and buttons. */
  storyId: UUID | null;
  openStory: (id: UUID, opts?: { intent?: StoryIntent }) => void;
  /**
   * Why the story was opened, when it was opened for a reason: 'play' starts
   * the recording the moment the panel arrives (you pressed a play button, so
   * it plays), 'edit' opens its edit dialog at once. Read once by the panel and
   * cleared.
   */
  storyIntent: StoryIntent | null;
  clearStoryIntent: () => void;
  closeStory: () => void;
  /** Which recipe is open to be read — a panel over wherever you were, like a story. */
  recipeId: UUID | null;
  openRecipe: (id: UUID) => void;
  closeRecipe: () => void;
  /** The recipe editor, when it is open, and what it was opened for. */
  recipeEditor: RecipeEditorRequest | null;
  openRecipeEditor: (request?: RecipeEditorRequest) => void;
  closeRecipeEditor: () => void;
  /**
   * Whose constellation is open: a day's (a simcha, a story, a birth) or a
   * person's. Both are drawn by the same screen — one circle in the middle and
   * everything that belongs to it floating around it.
   */
  memorySubject: MemorySubject | null;
  /**
   * Where on the screen the little node was that opened this day, so the big
   * one can grow out of it and go back to it (features/photos/MemoryNodes).
   * Absent when the day was opened from a link, which has nowhere to come from.
   */
  memoryOrigin: NodeOrigin | null;
  /**
   * How deep the constellations are stacked: 1 is a person or a day opened from
   * a screen, 2 is one of their days opened inside that. The screen itself reads
   * it to know what is *underneath* — going back from the last level hands the
   * tree (or the globe, or the timeline) straight back, while going back from a
   * nested one only returns to the constellation above it.
   */
  memoryDepth: number;
  /**
   * Tap the sky to go back.
   *
   * In a constellation the empty space around the ring is most of the screen,
   * and on a phone a tap there means "I am done looking at this" far more often
   * than it means anything else. So by default it goes back one level, exactly
   * as the corner does. It is a setting because it is a *gesture*: it cannot be
   * seen, and somebody who keeps dismissing the screen by accident while
   * reaching for a picture needs a way to turn it off.
   */
  tapOutsideBack: boolean;
  setTapOutsideBack: (on: boolean) => void;
  /**
   * The tabs whose description has been closed (see app/tabTips.ts and TabTip).
   *
   * Kept on the device like every other preference here — it is about what this
   * pair of eyes has already read, not about the account — and kept as the ones
   * *closed* rather than the ones still open, so a tab added next year introduces
   * itself to everybody without anything having to be migrated.
   */
  closedTabTips: readonly string[];
  closeTabTip: (id: string) => void;
  /** Settings → Show the tab descriptions again. */
  resetTabTips: () => void;
  openMemories: (subject: MemorySubject, from?: NodeOrigin | null) => void;
  closeMemories: () => void;
  /** Put every overlay away (the Shell does this when the screen changes). */
  closeOverlays: () => void;
  /** The Add tab's sheet / dialogs. */
  adding: AddKind | null;
  openAdd: (k: AddKind) => void;
  closeAdd: () => void;
  /** Shabbat mode: no notifications or nudges while it is Shabbat where you are. */
  shabbatMode: boolean;
  setShabbatMode: (on: boolean) => void;
  /** People for whom it is Shabbat right now, from where each of them is today. */
  shabbatIds: Set<UUID>;
  /** Shabbat right now where *you* are (your home or visit; the device's clock when unknown). */
  shabbatNow: boolean;
  /** Shabbat mode is on and it is Shabbat for you: keep quiet. */
  shabbatQuiet: boolean;
  /** The current minute, for clocks in the UI (local time where a relative is). Changes at most once a minute. */
  now: Date;
  /** Today, YYYY-MM-DD in the device's zone. */
  today: string;
  /** Coming up: birthdays, anniversaries, yahrzeits and chagim in the next weeks, soonest first (derived, not stored). */
  occasions: Occasion[];
  /** The personal occasion each person has today (the mark on their circle). */
  occasionToday: Map<UUID, Occasion>;
}

/** How far ahead "Coming up" looks. */
export const OCCASION_DAYS_AHEAD = 30;

const AppStateContext = createContext<AppStateValue | null>(null);

/*
 * What the family's data is loaded *as*, and which of the backend's tables each
 * part is read from.
 *
 * Every committed change used to reload all of it — twenty lists, each handed
 * to React as a brand-new array, so every screen re-rendered and every sum
 * worked out from any of them (the tree's layout, the badges, the timeline's
 * order) was worked out again. Somebody tapping a heart under a photograph
 * re-laid-out the family tree. It did not show with thirty people; with a
 * thousand, and a family that is actually using the app, it is a stutter on
 * every phone every time anybody anywhere does anything.
 *
 * A change arrives saying which tables it touched (`SyncRepository.onChange`),
 * so only the parts read from those tables are fetched again, and a list that
 * was not fetched **stays the very same array** — which is what lets everything
 * downstream of it sit still. Anything that leaves no doubt — a different
 * session, Admin mode, a reset, a `'*'` — reloads the lot, as before.
 *
 * When a part starts depending on another table (a list that is filtered by
 * somebody's role, say), that table belongs in its row here.
 */
const SLICES = {
  people: ['persons', 'person_names'],
  relationships: ['relationships'],
  // Archived stories are an administrator's to see, and drafts their writer's: who you are is part of the answer.
  stories: ['stories', 'family_memberships'],
  contributions: ['story_contributions'],
  storyPeople: ['story_people'],
  questions: ['story_questions'],
  recipes: ['recipes', 'family_memberships', 'person_account_links', 'profile_management_grants'],
  grants: ['profile_management_grants'],
  memberships: ['family_memberships'],
  notifications: ['notifications'],
  links: ['person_account_links'],
  portraits: ['person_portraits'],
  stars: ['person_stars'],
  treeSettings: ['tree_settings'],
  // A picture on a recipe the viewer cannot open is left out, so a recipe changing hands changes this list too.
  moments: ['family_photos', 'recipes', 'family_memberships'],
  places: ['person_places'],
  locations: ['locations'],
  approvals: ['person_approvals'],
  family: ['families'],
  review: ['change_requests', 'duplicate_suggestions'],
  pendingOps: ['pending_operations'],
} as const;
type Slice = keyof typeof SLICES;
const ALL_SLICES = Object.keys(SLICES) as Slice[];

/** How long changes are gathered before they are loaded: two hundred wedding photographs saved one after another are one reload, not two hundred. */
const GATHER_MS = 60;
/** How long after a load that failed it is tried again. */
const RETRY_LOAD_MS = 5000;

export function AppStateProvider({ children }: { children: ReactNode }) {
  const repos = useRepos();
  const { setLang } = useI18n();
  const [session, setSessionState] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [dataLoading, setDataLoading] = useState(true);
  const [online, setOnline] = useState(true);
  const [pendingOps, setPendingOps] = useState(0);

  const [motion, setMotion] = useLocalState<MotionPref>('edry.motion', 'system');
  const [textSize, setTextSize] = useLocalState<TextSizePref>('edry.textSize', 'normal');
  const [shabbatMode, setShabbatMode] = useLocalState<boolean>('edry.shabbatMode', false);
  // On unless it is turned off: the gesture is the point, and nobody goes looking for it in Settings.
  const [tapOutsideBack, setTapOutsideBack] = useLocalState<boolean>('edry.tapOutsideBack', true);
  const [closedTabTips, setClosedTabTips] = useLocalState<string[]>('edry.tabTips.closed', []);
  const closeTabTip = useCallback((id: string) => setClosedTabTips((prev) => (prev.includes(id) ? prev : [...prev, id])), [setClosedTabTips]);
  const resetTabTips = useCallback(() => setClosedTabTips([]), [setClosedTabTips]);
  const [storedExperience, setExperienceState] = useLocalState<ExperienceMode>('edry.experience', DEFAULT_EXPERIENCE);
  // Anything unexpected in storage falls back to the default rather than guessing.
  const experience: ExperienceMode = isExperienceMode(storedExperience) ? storedExperience : DEFAULT_EXPERIENCE;
  const setExperience = useCallback((m: ExperienceMode) => setExperienceState(m), [setExperienceState]);
  const has = useCallback((feature: Feature) => isFeatureOn(experience, feature), [experience]);
  // Admin mode lives with the session (repos.auth), not in a React state of its own: the
  // repositories read it too, so the switch has to change what the session may do, not just the UI.
  const [adminMode, setAdminModeState] = useState<boolean>(() => repos.auth.getAdminMode());
  const [panel, setPanel] = useState<PanelKind | null>(null);
  const [adding, setAdding] = useState<AddKind | null>(null);
  const [personCardId, setPersonCardId] = useState<UUID | null>(null);
  const [profileId, setProfileId] = useState<UUID | null>(null);
  /*
   * Constellations nest: a person, one of their days, and one day inside that.
   * So this is a stack rather than a single value — going back takes you up one
   * level and lands you where you came from, instead of dropping you out of the
   * whole thing onto the tree.
   */
  const [memoryStack, setMemoryStack] = useState<Array<{ subject: MemorySubject; origin: NodeOrigin | null }>>([]);
  const top = memoryStack[memoryStack.length - 1] ?? null;
  const memorySubject = top?.subject ?? null;
  const memoryOrigin = top?.origin ?? null;
  const memoryDepth = memoryStack.length;
  const [storyId, setStoryId] = useState<UUID | null>(null);
  const [recipeId, setRecipeId] = useState<UUID | null>(null);
  const [recipeEditor, setRecipeEditor] = useState<RecipeEditorRequest | null>(null);

  const [people, setPeople] = useState<PersonWithNames[]>([]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [stories, setStories] = useState<Story[]>([]);
  const [contributions, setContributions] = useState<StoryContribution[]>([]);
  const [storyPeople, setStoryPeople] = useState<StoryPerson[]>([]);
  const [questions, setQuestions] = useState<StoryQuestion[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [grants, setGrants] = useState<ProfileManagementGrant[]>([]);
  const [links, setLinks] = useState<PersonAccountLink[]>([]);
  const [portraits, setPortraits] = useState<PersonPortrait[]>([]);
  const [stars, setStars] = useState<Set<UUID>>(new Set());
  const [moments, setMoments] = useState<FamilyPhoto[]>([]);
  const [places, setPlaces] = useState<PersonPlace[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [treeSettings, setTreeSettings] = useState<TreeSettings>({ familyId: '', ...DEFAULT_TREE_SETTINGS, updatedAt: '' });
  const [governance, setGovernance] = useState<Governance>(DEFAULT_GOVERNANCE);
  const [approvals, setApprovals] = useState<PersonApproval[]>([]);
  const [memberships, setMemberships] = useState<FamilyMembership[]>([]);
  const [family, setFamily] = useState<Family | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const [readyFor, setReadyFor] = useState<UUID | null>(null);
  const [dataVersion, setDataVersion] = useState(0);

  /*
   * What the next load has to fetch. Changes that arrive close together are
   * gathered into one load (GATHER_MS); `asked` is a screen saying "I changed
   * something, look again" without being able to say what.
   */
  const wanted = useRef<Set<Slice>>(new Set(ALL_SLICES));
  const asked = useRef(false);
  const gather = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadSoon = useCallback(() => {
    if (gather.current !== null) return;
    gather.current = setTimeout(() => {
      gather.current = null;
      setTick((x) => x + 1);
    }, GATHER_MS);
  }, []);
  useEffect(
    () => () => {
      if (gather.current !== null) clearTimeout(gather.current);
      // Forgotten as well as cancelled: a guard left saying "one is already on its way" would block every load after a hot reload.
      gather.current = null;
    },
    [],
  );
  const changed = useCallback(
    (tables: readonly string[]) => {
      const all = tables.includes('*');
      for (const s of ALL_SLICES) if (all || SLICES[s].some((t) => tables.includes(t))) wanted.current.add(s);
      if (wanted.current.size) loadSoon();
    },
    [loadSoon],
  );
  /*
   * A screen that has just saved something asks for this, and nearly always the
   * save has *already* said which tables it touched (the backend announces its
   * own writes), so the load that follows fetches just those. Only when nothing
   * was announced — a backend whose realtime is down — is this taken at its
   * word and everything fetched, which is what it always used to mean.
   */
  const reload = useCallback(() => {
    asked.current = true;
    loadSoon();
  }, [loadSoon]);

  const refreshSession = useCallback(async () => {
    const s = await repos.auth.getSession();
    setSessionState(s);
    setLoading(false);
    if (s) {
      const account = await repos.auth.getAccount(s.accountId);
      if (account) setLang(account.preferredLang);
    }
  }, [repos, setLang]);

  useEffect(() => {
    void refreshSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Motion + text-size preferences on <html>
  useEffect(() => {
    document.documentElement.setAttribute('data-motion', motion);
  }, [motion]);
  useEffect(() => {
    document.documentElement.setAttribute('data-text', textSize);
  }, [textSize]);
  // The mode on <html> lets stylesheets hide or simplify things too (e.g. [data-experience="simple"]).
  useEffect(() => {
    document.documentElement.setAttribute('data-experience', experience);
  }, [experience]);
  // Admin mode on <html> is what paints every administrator control in the
  // admin colour — one attribute rather than a prop threaded through the app.
  useEffect(() => {
    document.documentElement.setAttribute('data-admin', adminMode ? 'on' : 'off');
  }, [adminMode]);

  /*
   * Dark, and only dark. The attribute drives the token set in
   * styles/tokens.css; it is written once rather than left off so that anyone
   * whose browser still holds an old `edry.theme` of `light` or `system` — and
   * whose index.html bootstrap may have acted on it — lands back on dark.
   */
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', '#14110d');
    try {
      localStorage.removeItem('edry.theme');
    } catch {
      /* private mode, cleared storage — the attribute above is what matters */
    }
  }, []);

  const [systemReduced, setSystemReduced] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setSystemReduced(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  const reducedMotionActive = motion === 'reduce' || (motion === 'system' && systemReduced);

  /*
   * Who the lists are for. A different session — somebody else signing in, the
   * same person switching Admin mode on — means every list may read differently,
   * so all of it is wanted again; and a load still on its way for the *previous*
   * viewer is told it is no longer wanted (`generation`) rather than being
   * allowed to land on top of theirs. Declared before the load below on purpose:
   * effects run in order, and the load must find everything already asked for.
   */
  const generation = useRef(0);
  const loadingNow = useRef(false);
  const retry = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (retry.current !== null) clearTimeout(retry.current);
      retry.current = null;
    },
    [],
  );
  useEffect(() => {
    generation.current += 1;
    loadingNow.current = false;
    for (const s of ALL_SLICES) wanted.current.add(s);
  }, [repos, session]);

  // Live data
  useEffect(() => {
    if (!session) {
      setDataLoading(false);
      return;
    }
    /*
     * One load at a time. A change that arrives while one is on its way is not
     * a reason to start again — under a burst (a wedding's worth of uploads) that
     * is a load which is restarted for ever and never lands. It is noted in
     * `wanted`, and the load in flight looks again the moment it has landed.
     */
    if (loadingNow.current) return;
    // Take what is wanted, and ask for exactly that. Nothing announced but a screen asked: everything (see `reload`).
    const want = new Set(wanted.current.size || !asked.current ? wanted.current : ALL_SLICES);
    if (!want.size) return;
    wanted.current = new Set();
    asked.current = false;
    const mine = generation.current;
    loadingNow.current = true;
    const reviewer = session.role === 'editor' || session.role === 'admin';
    const get = <T,>(slice: Slice, read: () => Promise<T>): Promise<T | undefined> => (want.has(slice) ? read() : Promise.resolve(undefined));
    void (async () => {
      let failed = false;
      try {
        // Asked for together and handed to React together: one render for the lot, however many parts there were.
        const [p, r, st, c, g, m, n, lk, pt, sr, ts, mo, pl, lo, sp, qs, ap, fam, gv, rc, crs, dups, ops] = await Promise.all([
          get('people', () => repos.people.list()),
          get('relationships', () => repos.relationships.list()),
          get('stories', () => repos.stories.visibleTo(session.accountId)),
          get('contributions', () => repos.stories.listContributions()),
          get('grants', () => repos.family.listGrants()),
          get('memberships', () => repos.family.listMemberships()),
          get('notifications', () => repos.family.listNotifications(session.accountId)),
          get('links', () => repos.people.listLinks()),
          get('portraits', () => repos.people.listAllPortraits()),
          get('stars', () => repos.people.listStars()),
          get('treeSettings', () => repos.admin.getTreeSettings()),
          get('moments', () => repos.photos.list()),
          get('places', () => repos.places.listPlaces()),
          get('locations', () => repos.family.listLocations()),
          get('storyPeople', () => repos.stories.listStoryPeople()),
          get('questions', () => repos.stories.listQuestions()),
          get('approvals', () => repos.people.listApprovals()),
          get('family', () => repos.family.getFamily()),
          // Who runs the family is kept on the family's own record.
          get('family', () => repos.admin.getGovernance()),
          get('recipes', () => repos.recipes.visibleTo(session.accountId)),
          get('review', () => (reviewer ? repos.admin.listChangeRequests('pending') : Promise.resolve([]))),
          get('review', () => (reviewer ? repos.admin.listDuplicates() : Promise.resolve([]))),
          get('pendingOps', () => repos.sync.listPendingOperations()),
        ]);
        // For somebody who is no longer looking: everything was asked for again the moment they changed.
        if (mine !== generation.current) return;
        if (p) setPeople(p);
        if (r) setRelationships(r);
        if (st) setStories(st);
        if (c) setContributions(c);
        if (sp) setStoryPeople(sp);
        if (qs) setQuestions(qs);
        if (rc) setRecipes(rc);
        if (g) setGrants(g);
        if (lk) setLinks(lk);
        if (pt) setPortraits(pt);
        if (sr) setStars(new Set(sr.map((x) => x.personId)));
        if (ts) setTreeSettings(ts);
        if (ap) setApprovals(ap);
        if (gv) setGovernance(gv);
        if (mo) setMoments(mo);
        if (pl) setPlaces(pl);
        if (lo) setLocations(lo);
        if (m) setMemberships(m);
        if (want.has('family')) setFamily(fam ?? null);
        if (n) setNotifications(n);
        if (crs && dups) setPendingReviewCount(crs.length + dups.length);
        if (ops) setPendingOps(ops.length);
        setDataLoading(false);
        setReadyFor(session.accountId);
        setDataVersion((v) => v + 1);
      } catch (e) {
        /*
         * A read that failed (a real backend, a bad connection). What was asked
         * for is put back, so the next change — or the next screen that asks —
         * fetches it again. It is not retried at once, which on a dead connection
         * would be a loop; it is tried again in a few seconds, so an app that
         * opened in a tunnel is not left saying "loading" until somebody reloads it.
         */
        failed = true;
        if (mine === generation.current) {
          for (const s of want) wanted.current.add(s);
          if (retry.current !== null) clearTimeout(retry.current);
          retry.current = setTimeout(() => {
            retry.current = null;
            setTick((x) => x + 1);
          }, RETRY_LOAD_MS);
        }
        console.warn('Could not load the family data', e);
      } finally {
        if (mine === generation.current) {
          loadingNow.current = false;
          // Something changed while this was on its way: look again now.
          if (!failed && (wanted.current.size || asked.current)) setTick((x) => x + 1);
        }
      }
    })();
  }, [repos, session, tick]);

  // Realtime: a committed change reloads the parts of the shared data it touched
  useEffect(() => repos.sync.onChange((tables) => changed(tables)), [repos, changed]);

  // Online / offline and offline-queue replay
  useEffect(() => {
    setOnline(repos.sync.isOnline());
    return repos.sync.onOnlineChange((v) => {
      setOnline(v);
      if (v) {
        void repos.sync.replayPending().then((results) => {
          if (results.length) changed(['*']);
        });
      }
    });
  }, [repos, reload]);

  // Session may gain a person link after an editor approves a claim
  useEffect(() => {
    if (!session) return;
    void repos.auth.getSession().then((s) => {
      if (s && (s.personId !== session.personId || s.role !== session.role)) setSessionState(s);
    });
  }, [repos, tick, session]);

  const dataReady = !!session && readyFor === session.accountId;
  const peopleById = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const locationsById = useMemo(() => new Map(locations.map((l) => [l.id, l])), [locations]);
  const graph = useMemo(() => buildGraph(people.map((p) => p.id), relationships), [people, relationships]);
  const me = useMemo(() => (session?.personId ? peopleById.get(session.personId) ?? null : null), [session, peopleById]);
  const actor = useMemo<Actor | null>(
    () => (session ? { session, grants: grants.filter((g) => g.helperAccountId === session.accountId && g.status === 'active') } : null),
    [session, grants],
  );
  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);
  // Circles still waiting for the family to recognise them.
  const awaitingRecognition = useMemo(() => new Set(people.filter((p) => p.reviewStatus === 'pending').map((p) => p.id)), [people]);
  const recognitionOf = useCallback(
    (personId: UUID) => {
      const mine = approvals.filter((a) => a.personId === personId);
      return {
        count: mine.length,
        needed: governance.memberApprovalsRequired,
        approved: !awaitingRecognition.has(personId),
        mine: !!session && mine.some((a) => a.accountId === session.accountId),
      };
    },
    [approvals, governance, awaitingRecognition, session],
  );
  /*
   * Account → circle, looked up rather than searched for. "Is this from somebody
   * I starred?" is asked of every notification — and again, twice per
   * comparison, to sort them — so a `links.find` here was the bell taking a
   * fifth of a second to open once it had a few thousand lines in it.
   */
  const personIdByAccount = useMemo(() => {
    const out = new Map<UUID, UUID>();
    for (const l of links) if (l.status === 'active' && !out.has(l.accountId)) out.set(l.accountId, l.personId);
    return out;
  }, [links]);
  const isProminent = useCallback(
    (n: Notification) => {
      // Events, someone coming to town and a question for you always deserve attention.
      if (n.kind === 'event' || n.kind === 'visit' || n.kind === 'question') return true;
      if (!n.actorAccountId) return false;
      const personId = personIdByAccount.get(n.actorAccountId);
      return !!personId && stars.has(personId);
    },
    [personIdByAccount, stars],
  );
  const prominentUnreadCount = useMemo(() => notifications.filter((n) => !n.read && isProminent(n)).length, [notifications, isProminent]);

  const canBeAdmin = !!session?.adminAvailable;
  /** Turning it on needs an administrator; the session is re-read so every rule sees the new role. */
  const setAdminMode = useCallback(
    (on: boolean) => {
      void repos.auth.setAdminMode(on).then(async (now) => {
        setAdminModeState(now);
        // A new session object: an administrator sees things a member does not (archived stories, recipes
        // taken down), and the effect above loads all of it again for whoever is now looking.
        setSessionState(await repos.auth.getSession());
      });
    },
    [repos],
  );

  // No reload of its own: a different session is what reloads everything (see `loadedFor`).
  const setSession = useCallback((s: Session | null) => setSessionState(s), []);

  // A panel that is not part of the current mode simply does not open (notifications in Simple).
  // The mode is read through a ref so that `openPanel` keeps its identity: the Shell closes the
  // open panel whenever this function changes, which used to shut Settings the moment the mode
  // was switched from inside it.
  const experienceRef = useRef(experience);
  experienceRef.current = experience;
  const openPanel = useCallback((p: PanelKind) => {
    if (p === 'notifications' && !isFeatureOn(experienceRef.current, 'notifications')) return;
    setPanel(p);
  }, []);
  const closePanel = useCallback(() => setPanel(null), []);
  // Switching to Simple while an advanced-only panel is open closes it.
  useEffect(() => {
    if (panel === 'notifications' && !isFeatureOn(experience, 'notifications')) setPanel(null);
  }, [experience, panel]);
  const openAdd = useCallback((k: AddKind) => setAdding(k), []);
  const closeAdd = useCallback(() => setAdding(null), []);

  /*
   * These all open over whatever you are reading, and they stack: a story from
   * a person's card, a face from inside that story, a picture from inside it
   * again — each on top of the last, each closing back to it. They are real
   * modals in the browser's top layer, so the order takes care of itself.
   *
   * The one pair that is a path rather than a stack is the quick card and the
   * full profile: "View profile" *replaces* the card, because they are two
   * depths of the same look at one person, not two things to pile up.
   */
  const openPersonCard = useCallback((id: UUID) => setPersonCardId(id), []);
  const closePersonCard = useCallback(() => setPersonCardId(null), []);
  const openProfile = useCallback((id: UUID) => {
    setPersonCardId(null);
    setProfileId(id);
  }, []);
  const closeProfile = useCallback(() => setProfileId(null), []);
  const [storyIntent, setStoryIntent] = useState<StoryIntent | null>(null);
  const openStory = useCallback((id: UUID, opts?: { intent?: StoryIntent }) => {
    setStoryIntent(opts?.intent ?? null);
    setStoryId(id);
  }, []);
  const clearStoryIntent = useCallback(() => setStoryIntent(null), []);
  const closeStory = useCallback(() => setStoryId(null), []);
  const openRecipe = useCallback((id: UUID) => setRecipeId(id), []);
  const closeRecipe = useCallback(() => setRecipeId(null), []);
  const openRecipeEditor = useCallback((request: RecipeEditorRequest = {}) => setRecipeEditor(request), []);
  const closeRecipeEditor = useCallback(() => setRecipeEditor(null), []);
  const openMemories = useCallback((subject: MemorySubject, from?: NodeOrigin | null) => {
    setMemoryStack((st) => {
      /*
       * Opening what is already on top *replaces* that level instead of adding
       * another one.
       *
       * Two identical levels look like one screen — the constellation is keyed
       * by `kind:id`, so it does not even remount — and Back then pops the
       * invisible copy and appears to do nothing at all. That is a dead end with
       * no way out but closing the app, and Jay found it: a person's card opened
       * from inside their own constellation offered a button back to that same
       * constellation. The button is gone, but the shape of the mistake is easy
       * to make again from a notification link or a deep link, so the stack
       * refuses to hold the same thing twice in a row.
       */
      const top = st[st.length - 1];
      const next = { subject, origin: from ?? null };
      if (top && top.subject.kind === subject.kind && top.subject.id === subject.id) return [...st.slice(0, -1), next];
      return [...st, next];
    });
  }, []);
  /** Back: up one level, not all the way out. */
  const closeMemories = useCallback(() => setMemoryStack((st) => st.slice(0, -1)), []);
  const [welcomeOpen, setWelcomeOpen] = useState<'news' | 'again' | null>(null);
  const openWelcome = useCallback((why: 'news' | 'again' = 'again') => setWelcomeOpen(why), []);
  const closeWelcome = useCallback(() => setWelcomeOpen(null), []);

  const closeOverlays = useCallback(() => {
    setPersonCardId(null);
    setProfileId(null);
    setStoryId(null);
    setRecipeId(null);
    // The editor saves as it goes, so putting it away costs nothing that was written.
    setRecipeEditor(null);
    // The same empty stack when it is already empty: a fresh `[]` is a change to React, and this runs on every
    // change of screen — which made every screen render twice on arrival.
    setMemoryStack((st) => (st.length ? [] : st));
  }, []);

  // Shabbat: re-checked every minute (candle lighting and nightfall are minute-precise events).
  const [minute, setMinute] = useState(() => Math.floor(Date.now() / 60_000));
  useEffect(() => {
    const id = setInterval(() => setMinute(Math.floor(Date.now() / 60_000)), 15_000);
    return () => clearInterval(id);
  }, []);
  // Prototype: the admin can pretend it is Shabbat for some people (or everyone) to test the feature.
  const [sim, setSim] = useState(() => repos.prototype?.getSettings() ?? null);
  useEffect(() => repos.prototype?.onSettings(setSim), [repos]);
  const shabbatIds = useMemo(() => {
    const now = new Date(minute * 60_000);
    const out = new Set<UUID>();
    if (sim?.shabbatEveryone) {
      for (const p of people) if (p.status !== 'deceased') out.add(p.id);
      return out;
    }
    for (const id of sim?.shabbatPersonIds ?? []) out.add(id);
    const cache = new Map<UUID, boolean>();
    // Worked out once: it builds a date formatter, and doing that per person was most of the cost of this loop.
    const date = localDateISO(now);
    for (const p of people) {
      if (p.status === 'deceased') continue;
      const where = whereIsAt(p, places, date);
      if (!where) continue;
      let on = cache.get(where.locationId);
      if (on === undefined) {
        const loc = locationsById.get(where.locationId);
        on = !!loc && (loc.lat !== undefined || !!loc.timezone) && shabbatAt(loc.lat, loc.lng, loc.timezone, now).on;
        cache.set(where.locationId, on);
      }
      if (on) out.add(p.id);
    }
    return out;
  }, [people, places, locationsById, minute, sim]);
  const shabbatNow = useMemo(() => {
    if (me && shabbatIds.has(me.id)) return true;
    if (me) {
      const where = whereIsAt(me, places, localDateISO(new Date(minute * 60_000)));
      if (where && locationsById.get(where.locationId)?.lat !== undefined) return false;
    }
    // No known place: go by the device's own clock and zone.
    let tz: string | undefined;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      tz = undefined;
    }
    return shabbatAt(undefined, undefined, tz, new Date(minute * 60_000)).on;
  }, [me, shabbatIds, places, locationsById, minute]);
  const shabbatQuiet = shabbatMode && shabbatNow;

  // Coming up — recomputed when the day changes or the family data does.
  const now = useMemo(() => new Date(minute * 60_000), [minute]);
  const today = useMemo(() => localDateISO(now), [now]);
  const occasions = useMemo(() => upcomingOccasions({ people, relationships }, today, OCCASION_DAYS_AHEAD), [people, relationships, today]);
  const occasionToday = useMemo(() => occasionsToday(occasions), [occasions]);

  const value: AppStateValue = {
    session, loading, refreshSession, setSession, actor,
    people, peopleById, relationships, graph, stories, contributions, storyPeople, questions, recipes, grants, links, portraits, stars, places, locations, locationsById, treeSettings, moments, memberships, family, notifications,
    governance, approvals, awaitingRecognition, recognitionOf,
    unreadCount, prominentUnreadCount, isProminent, pendingReviewCount, dataLoading, dataReady, reload, dataVersion, personIdByAccount, me, online, pendingOps,
    motion, setMotion, textSize, setTextSize, reducedMotionActive,
    experience, setExperience, has, canBeAdmin, adminMode, setAdminMode,
    panel, openPanel, closePanel, welcomeOpen, openWelcome, closeWelcome, adding, openAdd, closeAdd,
    personCardId, openPersonCard, closePersonCard, profileId, openProfile, closeProfile, storyId, openStory, storyIntent, clearStoryIntent, closeStory, recipeId, openRecipe, closeRecipe, recipeEditor, openRecipeEditor, closeRecipeEditor, memorySubject, memoryOrigin, memoryDepth, tapOutsideBack, setTapOutsideBack, closedTabTips, closeTabTip, resetTabTips, openMemories, closeMemories, closeOverlays,
    shabbatMode, setShabbatMode, shabbatIds, shabbatNow, shabbatQuiet,
    now, today, occasions, occasionToday,
  };

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}

export function useApp(): AppStateValue {
  const v = useContext(AppStateContext);
  if (!v) throw new Error('AppStateProvider missing');
  return v;
}

/** Convenience: is this feature part of the current experience mode? */
export function useFeature(feature: Feature): boolean {
  return useApp().has(feature);
}

/** Convenience: current language for display of names. */
export function useDisplayLang(): Lang {
  return useI18n().lang;
}
